"use strict";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useUrlContext } from "contexts/UrlContext";
import { FiatCurrency, ORDERED_CURRENCIES } from "features/fiatCurrency/constants";
import { useLocalizationContext } from "features/language/LocalizationContext";
import { useCurrentLocale } from "features/language/hooks";
import { getFiatCurrencyComponents } from "utilities/src/format/localeBased";
export function getFiatCurrencyCode(currency) {
  return currency.toString();
}
export function useFiatCurrencyComponents(currency) {
  const locale = useCurrentLocale();
  const currencyCode = getFiatCurrencyCode(currency);
  const components = getFiatCurrencyComponents(locale, currencyCode);
  return components;
}
export function getFiatCurrencyName(t, currency) {
  const currencyToCurrencyName = {
    [FiatCurrency.AustralianDollar]: t("currency.aud"),
    [FiatCurrency.BrazilianReal]: t("currency.brl"),
    [FiatCurrency.CanadianDollar]: t("currency.cad"),
    [FiatCurrency.ChineseYuan]: t("currency.cny"),
    [FiatCurrency.Euro]: t("currency.eur"),
    [FiatCurrency.BritishPound]: t("currency.gbp"),
    [FiatCurrency.HongKongDollar]: t("currency.hkd"),
    [FiatCurrency.IndonesianRupiah]: t("currency.idr"),
    [FiatCurrency.IndianRupee]: t("currency.inr"),
    [FiatCurrency.JapaneseYen]: t("currency.jpy"),
    [FiatCurrency.SouthKoreanWon]: t("currency.krw"),
    [FiatCurrency.NigerianNaira]: t("currency.ngn"),
    [FiatCurrency.PakistaniRupee]: t("currency.pkr"),
    [FiatCurrency.RussianRuble]: t("currency.rub"),
    [FiatCurrency.SingaporeDollar]: t("currency.sgd"),
    [FiatCurrency.ThaiBaht]: t("currency.thb"),
    [FiatCurrency.TurkishLira]: t("currency.try"),
    [FiatCurrency.UkrainianHryvnia]: t("currency.uah"),
    [FiatCurrency.UnitedStatesDollar]: t("currency.usd"),
    [FiatCurrency.VietnameseDong]: t("currency.vnd")
  };
  const currencyToGlobalSymbol = {
    [FiatCurrency.AustralianDollar]: "$",
    [FiatCurrency.BrazilianReal]: "R$",
    [FiatCurrency.CanadianDollar]: "$",
    [FiatCurrency.ChineseYuan]: "\xA5",
    [FiatCurrency.Euro]: "\u20AC",
    [FiatCurrency.BritishPound]: "\xA3",
    [FiatCurrency.HongKongDollar]: "$",
    [FiatCurrency.IndonesianRupiah]: "Rp",
    [FiatCurrency.IndianRupee]: "\u20B9",
    [FiatCurrency.JapaneseYen]: "\xA5",
    [FiatCurrency.SouthKoreanWon]: "\u20A9",
    [FiatCurrency.NigerianNaira]: "\u20A6",
    [FiatCurrency.PakistaniRupee]: "Rs",
    [FiatCurrency.RussianRuble]: "\u20BD",
    [FiatCurrency.SingaporeDollar]: "$",
    [FiatCurrency.ThaiBaht]: "\u0E3F",
    [FiatCurrency.TurkishLira]: "\u20BA",
    [FiatCurrency.UkrainianHryvnia]: "\u20B4",
    [FiatCurrency.UnitedStatesDollar]: "$",
    [FiatCurrency.VietnameseDong]: "\u20AB"
  };
  const code = getFiatCurrencyCode(currency);
  const symbol = currencyToGlobalSymbol[currency];
  return { name: currencyToCurrencyName[currency], shortName: `${code} (${symbol})` };
}
export function useFiatCurrencyInfo(currency) {
  const { t } = useTranslation();
  const components = useFiatCurrencyComponents(currency);
  const { name, shortName } = getFiatCurrencyName(t, currency);
  const code = getFiatCurrencyCode(currency);
  return {
    ...components,
    name,
    shortName,
    code
  };
}
function useUrlLocalCurrency() {
  const { useParsedQueryString } = useUrlContext();
  const parsed = useParsedQueryString();
  const parsedLocalCurrency = parsed.cur;
  if (typeof parsedLocalCurrency !== "string") {
    return void 0;
  }
  const lowerCaseSupportedLocalCurrency = parsedLocalCurrency.toLowerCase();
  return ORDERED_CURRENCIES.find((localCurrency) => localCurrency.toLowerCase() === lowerCaseSupportedLocalCurrency);
}
export function useAppFiatCurrency() {
  const storeFiatCurrency = useSelector((state) => state.userSettings.currentCurrency);
  const urlFiatCurrency = useUrlLocalCurrency();
  return useMemo(() => urlFiatCurrency ?? storeFiatCurrency, [storeFiatCurrency, urlFiatCurrency]);
}
export function useAppFiatCurrencyInfo() {
  const currency = useAppFiatCurrency();
  return useFiatCurrencyInfo(currency);
}
export const useLocalFiatToUSDConverter = () => {
  const { convertFiatAmount } = useLocalizationContext();
  return useCallback(
    (fiatAmount) => {
      const { amount: USDInLocalCurrency } = convertFiatAmount(1);
      return USDInLocalCurrency ? fiatAmount / USDInLocalCurrency : void 0;
    },
    [convertFiatAmount]
  );
};
